import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft, faChevronRight,
  faSortUp, faSortDown, faEye, faPlus, faTimes, faFilter,
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { useTable, usePagination, useSortBy, Column } from 'react-table';
import { agentsApi } from '../../services/agentsApi';
import type { Validation } from '../../types/agents';
import PlannerTab from '../../components/agents/PlannerTab';

// Status dot colors matching mockup
const STATUS_DOT: Record<string, string> = {
  PENDING: 'bg-yellow-400',
  REQUESTED: 'bg-yellow-400',
  IN_PROGRESS: 'bg-blue-500',
  IN_DEVELOPMENT: 'bg-blue-500',
  TESTING: 'bg-green-400',
  COMPLETED: 'bg-green-500',
  LIVE: 'bg-green-500',
  FAILED: 'bg-red-500',
  CANCELLED: 'bg-gray-400',
};

const STATUS_TEXT: Record<string, string> = {
  PENDING: 'text-yellow-600',
  REQUESTED: 'text-yellow-600',
  IN_PROGRESS: 'text-blue-600',
  IN_DEVELOPMENT: 'text-blue-600',
  TESTING: 'text-green-600',
  COMPLETED: 'text-green-600',
  LIVE: 'text-green-600',
  FAILED: 'text-red-600',
  CANCELLED: 'text-gray-500',
};

function StatusBadge({ value }: { value: string }) {
  const dotCls = STATUS_DOT[value] || 'bg-gray-400';
  const textCls = STATUS_TEXT[value] || 'text-gray-600';
  const label = value?.replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
  return (
    <span className={`inline-flex items-center gap-1.5 text-sm font-medium ${textCls}`}>
      <span className={`w-2 h-2 rounded-full ${dotCls}`} />
      {label}
    </span>
  );
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '-';
  try {
    const d = new Date(dateStr);
    return d.toISOString().split('T')[0];
  } catch {
    return dateStr;
  }
}

const AgentsManagement: React.FC = () => {
  const navigate = useNavigate();
  const [validations, setValidations] = useState<Validation[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [chatOpen, setChatOpen] = useState(false);

  const fetchValidations = async () => {
    try {
      setLoading(true);
      const params: any = { limit: 50 };
      if (statusFilter) params.status = statusFilter;
      const { data } = await agentsApi.listValidations(params);

      let parsed: Validation[] = [];
      if (data?.content?.[0]?.text) {
        try {
          const inner = JSON.parse(data.content[0].text);
          parsed = inner.validations || inner || [];
        } catch {
          parsed = [];
        }
      } else if (Array.isArray(data)) {
        parsed = data;
      }
      setValidations(parsed);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to load agents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchValidations();
    const interval = setInterval(fetchValidations, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const columns: Column<Validation>[] = useMemo(
    () => [
      {
        Header: 'AGENT NAME',
        accessor: 'title',
        Cell: ({ value }: any) => (
          <span className="text-sm text-gray-900">{value}</span>
        ),
      },
      {
        Header: 'CLIENT',
        accessor: 'type',
        Cell: ({ value, row }: any) => (
          <span className="text-sm text-gray-600">
            {(row.original as any).clientName || value?.replace(/_/g, ' ') || '-'}
          </span>
        ),
      },
      {
        Header: 'STATUS',
        accessor: 'status',
        Cell: ({ value }: any) => <StatusBadge value={value} />,
      },
      {
        Header: 'CREATED',
        accessor: 'createdAt',
        Cell: ({ value }: any) => (
          <span className="text-sm text-gray-500">{formatDate(value)}</span>
        ),
      },
      {
        Header: 'ACTIONS',
        id: 'actions',
        Cell: ({ row }: any) => (
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/consultant/agents/${row.original.id}`);
            }}
            className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            <FontAwesomeIcon icon={faEye} className="text-xs" />
            View
          </button>
        ),
      },
    ],
    [navigate],
  );

  const tableInstance = useTable(
    {
      columns,
      data: validations,
      initialState: { pageSize: 15 } as any,
    },
    useSortBy,
    usePagination,
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageCount,
    nextPage,
    previousPage,
    state,
  } = tableInstance as any;

  return (
    <div className="p-6">
      {/* Main content */}
      <div>
        {/* Card container */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          {/* Header row */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
            <div className="flex items-center gap-4">
              <h1 className="text-xl font-bold text-gray-900">Your Agents</h1>
              <button
                onClick={() => setChatOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
              >
                <FontAwesomeIcon icon={faPlus} className="text-xs" />
                New Agent
              </button>
            </div>
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faFilter} className="text-gray-400 text-sm" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white text-gray-700 cursor-pointer"
              >
                <option value="">All Status</option>
                <option value="PENDING">Requested</option>
                <option value="IN_PROGRESS">In Development</option>
                <option value="COMPLETED">Live</option>
                <option value="FAILED">Failed</option>
              </select>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-hidden">
            <table {...getTableProps()} className="w-full">
              <thead>
                {headerGroups.map((hg: any) => (
                  <tr {...hg.getHeaderGroupProps()} className="border-b border-gray-100">
                    {hg.headers.map((col: any) => (
                      <th
                        {...col.getHeaderProps(col.getSortByToggleProps())}
                        className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none"
                      >
                        {col.render('Header')}
                        {col.isSorted && (
                          <FontAwesomeIcon
                            icon={col.isSortedDesc ? faSortDown : faSortUp}
                            className="ml-1 text-gray-400"
                          />
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {loading && validations.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="text-center py-16 text-gray-400 text-sm">
                      Loading agents...
                    </td>
                  </tr>
                ) : page.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="text-center py-16 text-gray-400 text-sm">
                      No agents found. Click "+ New Agent" to create one.
                    </td>
                  </tr>
                ) : (
                  page.map((row: any) => {
                    prepareRow(row);
                    return (
                      <tr
                        {...row.getRowProps()}
                        onClick={() => navigate(`/consultant/agents/${row.original.id}`)}
                        className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        {row.cells.map((cell: any) => (
                          <td {...cell.getCellProps()} className="px-6 py-4">
                            {cell.render('Cell')}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pageCount > 1 && (
            <div className="flex items-center justify-between px-6 py-3 border-t border-gray-100">
              <span className="text-xs text-gray-500">
                Page {(state as any).pageIndex + 1} of {pageCount}
              </span>
              <div className="flex gap-2">
                <button onClick={() => previousPage()} disabled={!canPreviousPage} className="px-2 py-1 text-sm border rounded disabled:opacity-40">
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                <button onClick={() => nextPage()} disabled={!canNextPage} className="px-2 py-1 text-sm border rounded disabled:opacity-40">
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create New Agent — slide-out chat panel */}
      {chatOpen && (
        <div className="fixed right-0 top-0 h-full w-[460px] bg-white border-l border-gray-200 shadow-2xl z-50 flex flex-col">
          {/* Panel header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
            <div>
              <h2 className="text-lg font-bold text-gray-900">Create New Agent</h2>
              <p className="text-sm text-gray-500">Describe what you need</p>
            </div>
            <button
              onClick={() => setChatOpen(false)}
              className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <FontAwesomeIcon icon={faTimes} className="text-lg" />
            </button>
          </div>

          {/* Chat body */}
          <div className="flex-1 overflow-hidden">
            <PlannerTab onAgentCreated={fetchValidations} />
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentsManagement;
